package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.shared.model.displayTitle
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Img

/** A model to pick: what the animation uses ([url]), its short name, a picture and the full prompt for the tooltip. */
class ModelChoice(val url: String, val title: String, val image: String?, val description: String)

/** The picture a work is shown with: its sketch or first reference image. */
fun AssetResponse.previewImage(): String? =
    (metadata["previewImageUrl"] ?: referenceImages.firstOrNull() ?: metadata["conceptImageUrl"])
        ?.takeIf { assetType == AssetType.MESH_3D && (it.startsWith("http") || it.startsWith("/") || it.startsWith("data:image")) }

fun AssetResponse.toModelChoice(url: String) = ModelChoice(url, displayTitle, previewImage(), prompt)

/**
 * A labelled row of small cards: the built-in choice (null, an icon) and the user's models with a thumbnail and a
 * short name; the full prompt shows as a tooltip.
 */
@Composable
fun ModelChoicePicker(
    title: String,
    builtInLabel: String,
    builtInIcon: AppIcon,
    choices: List<ModelChoice>,
    selected: String?,
    enabled: Boolean = true,
    onSelect: (String?) -> Unit
) {
    val palette = LocalAppTheme.current.colors
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        SpanText(title, Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap)) {
            ChoiceCard(builtInLabel, null, builtInIcon, builtInLabel, selected == null, enabled) { onSelect(null) }
            choices.forEach { choice ->
                ChoiceCard(choice.title, choice.image, AppIcon.CUBE, choice.description, selected == choice.url, enabled) { onSelect(choice.url) }
            }
        }
    }
}

@Composable
internal fun ChoiceCard(label: String, image: String?, icon: AppIcon, tooltip: String, isSelected: Boolean, enabled: Boolean, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Button(
        Modifier.width(96.px).padding(4.px).borderRadius(10.px)
            .border(if (isSelected) 2.px else 1.px, LineStyle.Solid, if (isSelected) palette.accent else palette.borderStrong)
            .backgroundColor(if (isSelected) palette.accent.toRgb().copy(alpha = 40) else Colors.Transparent)
            .color(palette.textPrimary)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .opacity(if (enabled) 1.0 else 0.6)
            .styleModifier { property("font-family", "inherit") }
            .onClick { if (enabled) onClick() }
            .toAttrs {
                attr("type", "button")
                attr("aria-pressed", isSelected.toString())
                attr("title", tooltip.take(300))
                if (!enabled) attr("disabled", "")
            }
    ) {
        Column(Modifier.fillMaxWidth().gap(4.px)) {
            Box(Modifier.fillMaxWidth().height(60.px).borderRadius(7.px).backgroundColor(palette.background).styleModifier { property("overflow", "hidden") }, contentAlignment = Alignment.Center) {
                if (image != null) {
                    Img(src = BackendEndpoint.resolveAssetUrl(image), attrs = Modifier.fillMaxWidth().fillMaxHeight().objectFit(ObjectFit.Cover).toAttrs { attr("alt", ""); attr("loading", "lazy") })
                } else {
                    Box(Modifier.color(palette.textSecondary)) { Icon(icon, size = 24) }
                }
            }
            SpanText(
                label,
                Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).fillMaxWidth().lineHeight(1.25).height(30.px)
                    .styleModifier {
                        property("display", "-webkit-box"); property("-webkit-line-clamp", "2"); property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden"); property("text-align", "center"); property("overflow-wrap", "anywhere")
                    }
            )
        }
    }
}
